import { Account, AccountTypeEnum } from '@/client/accounts';
import { SystemRoles, User } from '@/client/users';
import {
  isDemoModeActive,
  isDemoModeAllowedOnCurrentPage,
} from '@/utils/demo-mode';

export const isDemoRestrictedMode = (
  account: Account | null,
  user: User | null | undefined,
) =>
  (!!account &&
    account.type === AccountTypeEnum.DEMO &&
    !!user &&
    account.id !== user?.account.id &&
    !!user?.account.freeTrialEndsAt &&
    ![SystemRoles.SUPER_ADMIN, SystemRoles.DEVELOPER].includes(
      user.role?.code,
    )) ||
  (isDemoModeActive() && isDemoModeAllowedOnCurrentPage());

export const parseEnviourmentUrl = (): {
  keycloakUrl: string;
  apiUrl: string;
} => {
  // Force the config urls so plugins work
  if (process.env?.REACT_APP_FORCE_CONFIG_URLS === 'true') {
    return {
      keycloakUrl: process.env.REACT_APP_KEYCLOAK_URL as string,
      apiUrl: process.env.REACT_APP_API_URL as string,
    };
  }

  if (
    window.location.hostname.endsWith('cyber-pilot.local') ||
    window.location.hostname.endsWith('cyber-pilot.test')
  ) {
    return {
      keycloakUrl: process.env.REACT_APP_KEYCLOAK_URL as string,
      apiUrl: process.env.REACT_APP_API_URL as string,
    };
  }

  // Handle segregated environment: *.app.eu-stg.cyberpilot.io
  if (window.location.hostname.endsWith('.app.eu-stg.cyberpilot.io')) {
    return {
      keycloakUrl: 'https://login.app.eu-stg.cyberpilot.io/',
      apiUrl: 'https://api.app.eu-stg.cyberpilot.io/',
    };
  }

  // Handle segregated environment: *.app.eu.cyberpilot.io (scannet prod)
  if (window.location.hostname.endsWith('.app.eu.cyberpilot.io')) {
    return {
      keycloakUrl: 'https://login.app.eu.cyberpilot.io/',
      apiUrl: 'https://api.app.eu.cyberpilot.io/',
    };
  }

  switch (window.location.hostname) {
    case 'localhost': {
      return {
        keycloakUrl: process.env.REACT_APP_KEYCLOAK_URL as string,
        apiUrl: process.env.REACT_APP_API_URL as string,
      };
    }
    case 'app.eu-stg.cyberpilot.io': {
      return {
        keycloakUrl: 'https://login.app.eu-stg.cyberpilot.io/',
        apiUrl: 'https://api.app.eu-stg.cyberpilot.io/',
      };
    }
    case 'app.eu.cyberpilot.io': {
      return {
        keycloakUrl: 'https://login.app.eu.cyberpilot.io/',
        apiUrl: 'https://api.app.eu.cyberpilot.io/',
      };
    }
    case 'app.cyberpilot.io': {
      return {
        keycloakUrl: 'https://login.app.cyberpilot.io/',
        apiUrl: 'https://api.app.cyberpilot.io/',
      };
    }
    case 'awareness.itm8.com': {
      return {
        keycloakUrl: 'https://login.awareness.itm8.com/',
        apiUrl: 'https://api.awareness.itm8.com/',
      };
    }
    default: {
      const domainSplitted =
        window.location.hostname.replace('www.', '').split('.') || [];

      let keycloakUrl;
      let apiUrl;

      // Handle the specific case for preview subdomains
      if (domainSplitted[1] === 'preview') {
        // For preview domains, use environment variable for API URL
        domainSplitted.splice(0, 2); // Remove the first two elements (subdomain and 'preview')
        keycloakUrl =
          (process.env.REACT_APP_KEYCLOAK_URL as string) ||
          `https://login.${domainSplitted.join('.')}/`;
        apiUrl = process.env.REACT_APP_API_URL as string;
      } else {
        domainSplitted.shift(); // Remove the first element (subdomain)
        keycloakUrl = `https://login.${domainSplitted.join('.')}/`;
        apiUrl = `https://api.${domainSplitted.join('.')}/`;
      }

      return { keycloakUrl, apiUrl };
    }
  }
};
